import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Tags,
  Loader2,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle
} from "lucide-react";

export default function TiposVisitaPage() {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    color: "#6366f1",
    requiere_aprobacion: false,
    activo: true
  });
  
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/visitantes/tipos`, getAuthHeaders());
      setTipos(response.data || []);
    } catch (err) {
      toast.error("Error al cargar tipos de visita");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (tipo = null) => {
    if (tipo) {
      setEditingTipo(tipo);
      setFormData({
        nombre: tipo.nombre || "",
        descripcion: tipo.descripcion || "",
        color: tipo.color || "#6366f1",
        requiere_aprobacion: tipo.requiere_aprobacion || false,
        activo: tipo.activo !== false
      });
    } else {
      setEditingTipo(null);
      setFormData({
        nombre: "",
        descripcion: "",
        color: "#6366f1",
        requiere_aprobacion: false,
        activo: true
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      setSaving(true);
      
      if (editingTipo) {
        await axios.put(`${API}/visitantes/tipos/${editingTipo.id}`, formData, getAuthHeaders());
        toast.success("Tipo de visita actualizado");
      } else {
        await axios.post(`${API}/visitantes/tipos`, formData, getAuthHeaders());
        toast.success("Tipo de visita creado");
      }
      
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tipoId) => {
    try {
      await axios.delete(`${API}/visitantes/tipos/${tipoId}`, getAuthHeaders());
      toast.success("Tipo de visita eliminado");
      setShowDeleteConfirm(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleToggleActive = async (tipo) => {
    try {
      await axios.put(`${API}/visitantes/tipos/${tipo.id}`, {
        ...tipo,
        activo: !tipo.activo
      }, getAuthHeaders());
      toast.success(`Tipo ${!tipo.activo ? 'activado' : 'desactivado'}`);
      fetchData();
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="tipos-visita-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Tags className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Tipos de Visita
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Configura los motivos de visita
              </p>
            </div>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="nuevo-tipo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Tipo
          </Button>
        </div>

        {/* Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tipos.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center text-slate-500">
                <Tags className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No hay tipos de visita configurados</p>
                <Button 
                  onClick={() => handleOpenDialog()} 
                  variant="outline" 
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primer tipo
                </Button>
              </CardContent>
            </Card>
          ) : (
            tipos.map((tipo) => (
              <Card 
                key={tipo.id} 
                className={`relative overflow-hidden ${!tipo.activo ? 'opacity-60' : ''}`}
                data-testid={`tipo-card-${tipo.id}`}
              >
                <div 
                  className="absolute top-0 left-0 w-full h-1"
                  style={{ backgroundColor: tipo.color || '#6366f1' }}
                />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tipo.color || '#6366f1' }}
                        />
                        {tipo.nombre}
                      </CardTitle>
                      {tipo.descripcion && (
                        <CardDescription className="mt-1">{tipo.descripcion}</CardDescription>
                      )}
                    </div>
                    <Badge className={tipo.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                      {tipo.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {tipo.requiere_aprobacion && (
                        <Badge variant="outline" className="text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Requiere aprobación
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleToggleActive(tipo)}
                        title={tipo.activo ? 'Desactivar' : 'Activar'}
                      >
                        {tipo.activo ? (
                          <XCircle className="w-4 h-4 text-slate-400" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => handleOpenDialog(tipo)}
                      >
                        <Edit className="w-4 h-4 text-slate-400" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => setShowDeleteConfirm(tipo)}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingTipo ? 'Editar Tipo de Visita' : 'Nuevo Tipo de Visita'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Reunión de negocios"
                  data-testid="input-nombre-tipo"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción del tipo de visita..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#6366f1"
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between py-2 border-y">
                <div>
                  <Label>Requiere Aprobación</Label>
                  <p className="text-xs text-slate-500">La visita necesitará ser aprobada antes de proceder</p>
                </div>
                <Switch
                  checked={formData.requiere_aprobacion}
                  onCheckedChange={(v) => setFormData({ ...formData, requiere_aprobacion: v })}
                />
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Activo</Label>
                  <p className="text-xs text-slate-500">Disponible para seleccionar en el registro</p>
                </div>
                <Switch
                  checked={formData.activo}
                  onCheckedChange={(v) => setFormData({ ...formData, activo: v })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="guardar-tipo-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingTipo ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <p className="text-slate-600">
              ¿Está seguro de eliminar el tipo de visita <strong>{showDeleteConfirm?.nombre}</strong>?
              Esta acción no se puede deshacer.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button 
                onClick={() => handleDelete(showDeleteConfirm?.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
